import { GREEN_MATERIALS_MODULE_ID, MATERIAL_VENDORS_MODULE_ID } from './constant';
import { permissions } from './permission.constant';

// The four permissions a record module (Green Materials, Vendor) grants. Each is checked on its own by the
// backend; "All" is a superset there; in the role tree, ticking it also ticks the others.
interface ModulePermissionIds {
  all: string;
  edit: string;
  approve: string;
  readOnly: string;
}

const MODULE_PERMISSIONS: Record<string, ModulePermissionIds> = {
  [GREEN_MATERIALS_MODULE_ID]: {
    all: permissions.GREEN_MATERIALS_ALL.id,
    edit: permissions.GREEN_MATERIALS_EDIT.id,
    approve: permissions.GREEN_MATERIALS_APPROVE_RESPONSE.id,
    readOnly: permissions.GREEN_MATERIALS_READ_ONLY.id
  },
  [MATERIAL_VENDORS_MODULE_ID]: {
    all: permissions.VENDOR_ALL.id,
    edit: permissions.VENDOR_EDIT.id,
    approve: permissions.VENDOR_APPROVE_RESPONSE.id,
    readOnly: permissions.VENDOR_READ_ONLY.id
  }
};

export interface ModuleAccess {
  canView: boolean;
  // Responses list, approval status and history
  canSeeResponses: boolean;
  canEdit: boolean;
  // Still excludes the user's own responses (checked per response)
  canApprove: boolean;
  canCreate: boolean;
  // Read Only is the role's only permission in the module: list, detail and product enquiries only
  readOnly: boolean;
}

const NO_ACCESS: ModuleAccess = { canView: false, canSeeResponses: false, canEdit: false, canApprove: false, canCreate: false, readOnly: false };
const FULL_ACCESS: ModuleAccess = { canView: true, canSeeResponses: true, canEdit: true, canApprove: true, canCreate: true, readOnly: false };

// `granted` is the getPermissionValue map ({ uuid: true }). Super Admin bypasses every backend check.
export function moduleAccess(moduleId: string | null | undefined, granted: Record<string, boolean> | null, isSuperAdmin: boolean): ModuleAccess {
  if (isSuperAdmin) return FULL_ACCESS;
  const ids = moduleId ? MODULE_PERMISSIONS[moduleId] : undefined;
  if (!ids || !granted) return NO_ACCESS;

  const has = (id: string) => !!granted[id];
  const all = has(ids.all), edit = has(ids.edit), approve = has(ids.approve), readOnly = has(ids.readOnly);
  return {
    canView: all || edit || approve || readOnly,
    canSeeResponses: all || edit || approve,
    canEdit: all || edit,
    canApprove: all || approve,
    canCreate: all,
    readOnly: readOnly && !all && !edit && !approve
  };
}

// Any module whose responses the role may review — opens the response screens' route guard
export function canSeeAnyModuleResponses(granted: Record<string, boolean> | null, isSuperAdmin: boolean): boolean {
  return Object.keys(MODULE_PERMISSIONS).some(moduleId => moduleAccess(moduleId, granted, isSuperAdmin).canSeeResponses);
}

// Sidebar entries: shown when the role holds any permission of the module
export function modulePermissionList(moduleId: string): { id: string }[] {
  const ids = MODULE_PERMISSIONS[moduleId];
  return ids ? [ids.all, ids.edit, ids.approve, ids.readOnly].map(id => ({ id })) : [];
}

// Forms → Responses entry: All, Edit or Approve Response in any record module (Read Only never reviews responses)
export function moduleResponsePermissionList(): { id: string }[] {
  return Object.values(MODULE_PERMISSIONS).flatMap(ids => [ids.all, ids.edit, ids.approve]).map(id => ({ id }));
}

// Record modules whose responses the role may review, for scoping the responses list
export function reviewableModuleIds(granted: Record<string, boolean> | null, isSuperAdmin: boolean): string[] {
  return Object.keys(MODULE_PERMISSIONS).filter(moduleId => moduleAccess(moduleId, granted, isSuperAdmin).canSeeResponses);
}

// Edit Response: All or Edit in the response's module (Super Admin: any form), and only while the response is
// PENDING or REJECTED. Approved responses and forms without approval can't be edited.
export function canEditResponse(
  response: { form?: { moduleId: string | null } | null; approval?: { status: string } | null } | null | undefined,
  granted: Record<string, boolean> | null,
  isSuperAdmin: boolean
): boolean {
  const status = response?.approval?.status;
  if (status !== 'PENDING' && status !== 'REJECTED') return false;
  return moduleAccess(response?.form?.moduleId, granted, isSuperAdmin).canEdit;
}

// Record modules the role can open (any of the four permissions)
export function viewableModuleIds(granted: Record<string, boolean> | null, isSuperAdmin: boolean): string[] {
  return Object.keys(MODULE_PERMISSIONS).filter(moduleId => moduleAccess(moduleId, granted, isSuperAdmin).canView);
}

// Record modules the role may add records to (All), for the header "+" menu
export function creatableModuleIds(granted: Record<string, boolean> | null, isSuperAdmin: boolean): string[] {
  return Object.keys(MODULE_PERMISSIONS).filter(moduleId => moduleAccess(moduleId, granted, isSuperAdmin).canCreate);
}

// Tutorials are not a record module (no responses or approval), so they stay out of MODULE_PERMISSIONS.
// Every Tutorial permission opens the list; All also covers Add and Edit.
export function tutorialPermissionList(): { id: string }[] {
  return [permissions.TUTORIAL_ALL, permissions.TUTORIAL_ADD, permissions.TUTORIAL_EDIT, permissions.TUTORIAL_READ_ONLY];
}

export function tutorialAccess(granted: Record<string, boolean> | null, isSuperAdmin: boolean): { canAdd: boolean; canEdit: boolean } {
  const has = (permission: { id: string }) => isSuperAdmin || !!granted?.[permission.id];
  return {
    canAdd: has(permissions.TUTORIAL_ALL) || has(permissions.TUTORIAL_ADD),
    canEdit: has(permissions.TUTORIAL_ALL) || has(permissions.TUTORIAL_EDIT)
  };
}

// Project Stages is separate from the Project permissions, which still decide who may create or edit a project.
// Any of its permissions opens the stage configuration; All also covers Add and Edit.
export interface ProjectStagesAccess {
  canView: boolean;
  canAdd: boolean;
  canEdit: boolean;
}

export function projectStagesAccess(granted: Record<string, boolean> | null, isSuperAdmin: boolean): ProjectStagesAccess {
  const has = (permission: { id: string }) => isSuperAdmin || !!granted?.[permission.id];
  const all = has(permissions.PROJECT_STAGES_ALL), add = has(permissions.PROJECT_STAGES_ADD);
  const edit = has(permissions.PROJECT_STAGES_EDIT), readOnly = has(permissions.PROJECT_STAGES_READ_ONLY);
  return { canView: all || add || edit || readOnly, canAdd: all || add, canEdit: all || edit };
}

// Predefined roles are 1–9; any other ID is a custom role (the API has no isCustom flag)
export const MAX_SYSTEM_ROLE_ID = 9;
export function isSystemRole(roleId: number | null | undefined): boolean {
  return roleId != null && roleId >= 1 && roleId <= MAX_SYSTEM_ROLE_ID;
}
